import {debounce} from '../Utility/DebounceUtility';
import {closeSearchOverlay} from '../Navigation/NavSearchFormUtility';
import {LoadingAnimation} from '../Components/LoadingAnimation';

export class SearchSuggest {
  private readonly formSubmitSelector: string = '.js-suggestSubmit';
  private readonly formSubmitValueSelector: string = '.js-suggestSubmitValue';
  private readonly ajaxErrorMessageSelector: string = '.js-suggestError';
  private readonly hideClass: string = 'hide';
  private readonly ajaxUrl: string = '';
  private readonly loadingAnimation: LoadingAnimation;
  private readonly searchInput;
  private readonly searchForm;
  private readonly searchSuggest;
  private readonly searchContent;
  private readonly resetButton;
  private currentValue: string = '';
  private ajaxErrorMessage;
  private formSubmit;
  private formSubmitValue;
  private ajaxRequest;
  private suggestSlider: any = null;
  private loadMoreProducts: string = '';

  constructor(searchValue: string) {
    this.loadingAnimation = new LoadingAnimation('.js-loadingAnimationSearch');
    this.searchInput = document.querySelector('.js-searchInput');
    if (this.searchInput === null) {
      return;
    }
    this.searchInput.value = searchValue;
    this.searchContent = document.querySelector('.js-searchSuggestContent');
    this.searchForm = document.querySelector('.js-searchForm');
    this.searchSuggest = document.querySelector('.js-searchSuggest');
    this.resetButton = document.querySelector('.js-clearSearchInput');
    this.ajaxUrl = $(this.searchInput).data('ajax');
  }

  public initSuggestFlyout(): void {
    if (this.searchInput) {
      this.addChangeWatcher();
    }
    if (this.searchInput && this.searchInput.value.length > 0) {
      this.resetButton.classList.remove(this.hideClass);
    }
    if (this.searchContent && this.searchContent.firstChild) {
      this.openSuggestFlyout();
    }
  }

  public openSuggestFlyout(): void {
    if (this.searchInput.value.length > 0 && this.searchInput.value !== this.currentValue) {
      this.triggerSearchRequest();
    }
    if (this.searchInput) {
      this.resetButton.classList.remove(this.hideClass);
    }
    if (this.searchSuggest) {
      this.searchSuggest.classList.remove(this.hideClass);
    }
  }

  public closeSuggestFlyout(): void {
    this.hideErrorMessage();
    if (this.searchSuggest && !this.searchSuggest.classList.contains(this.hideClass)) {
      this.searchSuggest.classList.add(this.hideClass);
    }
    if (this.resetButton) {
      this.resetButton.classList.add(this.hideClass);
    }
  }

  public showErrorMessage(): void {
    if (!this.ajaxErrorMessage) {
      this.ajaxErrorMessage = document.querySelector(this.ajaxErrorMessageSelector);
    }
    this.ajaxErrorMessage.classList.remove(this.hideClass);
  }

  public hideErrorMessage(): void {
    if (this.ajaxErrorMessage) {
      this.ajaxErrorMessage.classList.add(this.hideClass);
    }
  }

  public clearInput() {
    if (this.searchInput) {
      this.searchInput.value = '';
    }
  }

  public removeSuggestFlyoutContent() {
    while (this.searchContent.firstChild) {
      this.searchContent.removeChild(this.searchContent.firstChild);
    }
    this.searchContent.classList.add(this.hideClass);
  }

  private addChangeWatcher(): void {
    this.searchInput.addEventListener('keyup', debounce(
      (event) => {
        // on "esc" close dialog
        if (event.which === 27) {
          closeSearchOverlay();
          this.loadingAnimation.hideLoadingAnimation();
          this.closeSuggestFlyout();
          return;
        }
        if (this.searchInput.value === this.currentValue || this.searchInput.value.length < 1) {
          return;
        }
        this.triggerSearchRequest();
      },
      400));

    this.searchInput.addEventListener('keyup', () => {
      if (this.searchInput.value.length > 0) {
        this.resetButton.classList.remove(this.hideClass);
      } else {
        this.resetButton.classList.add(this.hideClass);
      }
    });
  }

  private triggerSearchRequest(): void {
    this.currentValue = this.searchInput.value;
    while (this.searchContent.firstChild) {
      this.searchContent.removeChild(this.searchContent.firstChild);
    }
    this.closeSuggestFlyout();
    this.getSearchResult(this.searchInput.value);
  }

  private getSearchResult(query: string): void {
    if (this.ajaxRequest) {
      this.ajaxRequest.abort();
    }
    if (this.suggestSlider !== null) {
      this.suggestSlider.slick('unslick');
      this.suggestSlider = null;
    }
    this.loadingAnimation.showLoadingAnimation();
    this.ajaxRequest = $.ajax({
      url: this.ajaxUrl + '&q=' + query,
      dataType: 'json',
      success: result => {
        this.hideErrorMessage();
        this.fillBoxWithContent(result.html);
        this.loadMoreProducts = result.searchMore;
      },
      complete: () => {
        this.loadingAnimation.hideLoadingAnimation();
        this.bindSlider();
      },
      error: () => {
        if (this.ajaxRequest.statusText !== 'abort') {
          this.showErrorMessage();
        }
      },
    });
  }

  /**
   * @see documentation/Solr/Suggest
   */
  private bindSlider(): void {
    this.suggestSlider = $('.js-suggestSlider');
    this.suggestSlider.slick({
      slidesToShow: 4,
      slidesToScroll: 4,
      lazyLoad: 'ondemand',
      dots: false,
      arrows: true,
      infinite: false,
      prevArrow: $('.js-slider-prev'),
      nextArrow: $('.js-slider-next'),
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
          },
        },
      ],
    }).on('setPosition', (event, slick) => {
      slick.$slides.css('height', slick.$slideTrack.height() + 'px');
    });
    if (this.suggestSlider.slick('getSlick').slideCount < 4) {
      this.suggestSlider.removeClass('suggest__slider--small');
    } else {
      this.suggestSlider.addClass('suggest__slider--small');
    }
    this.suggestSlider.slick('reinit');
    this.suggestSlider.on('beforeChange', (event, slick, currentSlide, nextSlide) => {
      // the eight is nothing special, just some slides before the end: load new ones
      if (nextSlide + 8 > slick.slideCount) {
        $.ajax({
          url: this.loadMoreProducts,
          dataType: 'json',
          success: result => {
            // split our html to objects, ignore intermediate text elements
            let slides = $(result.html).filter('.suggest__product');
            for (let i = 0; i < slides.length; i++) {
              this.suggestSlider.slick('slickAdd', slides[i]);
            }
            this.loadMoreProducts = result.searchMore;
          },
          error: (jqXHR, statusText) => {
            if (statusText !== 'abort') {
              this.showErrorMessage();
            }
          },
        });
      }
    });
  }

  private fillBoxWithContent(content: string): void {
    this.searchContent.classList.remove(this.hideClass);
    if (this.searchContent.firstChild) {
      return;
    }
    this.loadingAnimation.hideLoadingAnimation();
    this.searchContent.insertAdjacentHTML('beforeend', content);
    this.formSubmit = this.searchContent.querySelector(this.formSubmitSelector);
    this.formSubmitValue = this.searchContent.querySelectorAll(this.formSubmitValueSelector);
    Array.prototype.forEach.call(this.formSubmitValue, (submitValueElement) => {
      submitValueElement.innerText = '"' + this.currentValue + '"';
    });
    if (this.formSubmit) {
      this.formSubmit.addEventListener('click', () => {
        this.searchForm.submit();
      });
    }
    this.openSuggestFlyout();
  }
}
